// eslint-disable-next-line
import React, { useEffect, useState } from 'react'

/**
 * Represents the maximum number of history entries to store.
 * Change this constant if there are performance issues or if you want to store more history.
 */
const MaxHistoryCount = 50

/**
 * Represents the type of the route history.
 */
export type RouteHistoryType = {
  route: string
  routeEntries: string[]
  isRouteInHistory: (route: string) => boolean
  isPreviousRouteInHistory: (route: string) => boolean
  clearRouteHistory: () => void
}
type routeType = {
  route: string
  params: string
}
/**
 * Custom hook that manages the route history.
 * It keeps track of the visited routes and provides methods to check if a route is in history
 * and to clear the route history.
 * @returns The route history object.
 */
export default function useRouteHistory(): RouteHistoryType {
  const routeStorageName = 'routeHistoryEntries'
  const route = window.location.pathname.split('/')[1]
  const params = window.location.pathname.split('/')[2]

  const routeHistoryEntries = sessionStorage.getItem(routeStorageName)
  const [routeHistoryArray, setRouteHistoryArray] = useState<routeType[]>(
    routeHistoryEntries ? JSON.parse(routeHistoryEntries) : [],
  )

  /**
   * Updates the route history with the given route.
   * If the history exceeds the maximum count, the oldest entry is removed.
   * @param route - The route to add to the history.
   */
  const updateRouteHistory = () => {
    if (
      routeHistoryArray.length > 1 &&
      route === routeHistoryArray[routeHistoryArray.length - 1].route
    )
      return
    routeHistoryArray.push({ route, params })
    if (routeHistoryArray.length >= MaxHistoryCount) {
      setRouteHistoryArray(routeHistoryArray.slice(1)) // pop the first record and add the new route history
    } else {
      setRouteHistoryArray(routeHistoryArray) // record the route history
    }
  }

  useEffect(() => {
    updateRouteHistory()
    sessionStorage.setItem(routeStorageName, JSON.stringify(routeHistoryArray))
  }, [route, params])

  /**
   * Checks if a route is present in the route history.
   * @param route - The route to check.
   * @returns `true` if the route is in history, `false` otherwise.
   */
  const isRouteInHistory = (route: string): boolean => {
    return routeHistoryArray.map((entry) => entry.route).includes(route)
  }

  /**
   * Checks if the previous route in the route history array matches the given route.
   *
   * @param route - The route to check against the previous route in the history.
   * @returns A boolean indicating whether the previous route matches the given route.
   */
  const isPreviousRouteInHistory = (route: string): boolean => {
    return (
      routeHistoryArray.length >= 2 &&
      routeHistoryArray[routeHistoryArray.length - 2].route === route
    )
  }

  /**
   * Clears the route history.
   * Removes all entries from the history and clears the storage.
   */
  const clearRouteHistory = () => {
    setRouteHistoryArray([]) // clear the route history array
    sessionStorage.removeItem(routeStorageName) // remove the route history from local storage
  }

  return {
    route,
    routeEntries: routeHistoryArray.map((entry) => entry.route),
    isRouteInHistory,
    isPreviousRouteInHistory,
    clearRouteHistory,
  }
}
